import React, { useState } from 'react';
import { Alert, Col, Input, Label, Row } from 'reactstrap';

import { formatCurrency } from '~libs/dataUtils';

import { SummaryStatCard } from '~components';
import Icon, { faChevronDown, faChevronRight, faExclamationTriangle, faFileInvoiceDollar, faMoneyBill, faUsers } from '~components/icon';
import { Column, TableView } from '~components/table-view-v3';

import { formatBatchDate } from '~direct-debits/lib/formatBatchDate';
import { PaymentBatchPreviewClient, PaymentBatchPreviewResponse } from '~direct-debits/models';

interface ComponentProps {
  value: { preview?: PaymentBatchPreviewResponse; acknowledged?: boolean };
  onChange?: (name: string, value: any) => void;
}

const INVOICE_COLUMNS: Column[] = [
  { id: 'reference', header: 'Invoice ID', type: 'text' },
  {
    id: 'invoice_date',
    header: 'Invoice Date',
    type: 'text',
    deriveValue: (row) => formatBatchDate(row.invoice_date as string | null),
    classNames: { header: 'cell-align-center', column: 'cell-align-center' },
  },
  {
    id: 'due_date',
    header: 'Due Date',
    type: 'text',
    deriveValue: (row) => formatBatchDate(row.due_date as string | null),
    classNames: { header: 'cell-align-center', column: 'cell-align-center' },
  },
  {
    id: 'amount',
    header: 'Amount',
    type: 'text',
    deriveValue: (row) => formatCurrency(row.amount as number),
    classNames: { header: 'cell-align-right', column: 'cell-align-right' },
  },
];

interface ClientCardProps {
  client: PaymentBatchPreviewClient;
}

const ClientCard: React.FC<ClientCardProps> = ({ client }) => {
  const [open, setOpen] = useState(false);
  const { client_id, client_name, invoice_count, total_amount, invoices } = client;
  const cardId = `payment-batches-wizard-client-${client_id}`;
  return (
    <div className="border rounded bg-white shadow-sm mb-3" data-cy={cardId}>
      <div
        className="d-flex align-items-center p-3 client-card-header"
        role="button"
        tabIndex={0}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen(!open);
          }
        }}
        aria-expanded={open}
        // Only emit `aria-controls` when the body is actually mounted, otherwise
        // the IDREF would dangle whenever the card is collapsed and strict ARIA
        // auditors would flag it.
        aria-controls={open ? `${cardId}-body` : undefined}
      >
        <Icon icon={open ? faChevronDown : faChevronRight} className="me-3 text-secondary" />
        <div className="fw-semibold">{client_name ?? `Client #${client_id}`}</div>
        <div className="text-muted ms-3">
          {invoice_count} invoice{invoice_count === 1 ? '' : 's'}
        </div>
        <div className="flex-grow-1" />
        <div className="fw-semibold">{formatCurrency(total_amount)}</div>
      </div>

      {open && (
        <div className="px-3 pb-3" id={`${cardId}-body`}>
          <TableView
            id={`${cardId}-invoices`}
            columns={INVOICE_COLUMNS}
            data={invoices}
            readonly
            striped
          />
        </div>
      )}
    </div>
  );
};

const Confirm: React.FC<ComponentProps> = ({ value, onChange }) => {
  const preview = value?.preview;
  const acknowledged = value?.acknowledged === true;

  // Defensive empty state — Step 1's `showNextButton` should prevent this from
  // ever rendering with no clients, but guard anyway so a stale preview doesn't
  // crash the wizard.
  if (!preview || preview.clients.length === 0) {
    return (
      <div className="text-muted" data-cy="payment-batches-wizard-confirm-empty">
        No preview data available. Return to the previous step and adjust the filters.
      </div>
    );
  }

  const { stats, clients } = preview;
  const averagePerClient = stats.client_count > 0 ? stats.total_amount / stats.client_count : 0;

  return (
    <div data-cy="payment-batches-wizard-confirm">
      <Row className="g-3 mb-3">
        <Col md={3} className="mb-2">
          <SummaryStatCard
            title="Clients"
            icon={<Icon icon={faUsers} />}
            count={<span>{stats.client_count}</span>}
            countClassName="text-primary"
          />
        </Col>
        <Col md={3} className="mb-2">
          <SummaryStatCard
            title="Invoices"
            icon={<Icon icon={faFileInvoiceDollar} />}
            count={<span>{stats.invoice_count}</span>}
            countClassName="text-primary"
          />
        </Col>
        <Col md={3} className="mb-2">
          <SummaryStatCard
            title="Total"
            icon={<Icon icon={faMoneyBill} />}
            count={<span>{formatCurrency(stats.total_amount)}</span>}
            countClassName="text-success"
          />
        </Col>
        <Col md={3} className="mb-2">
          <SummaryStatCard
            title="Avg per client"
            icon={<Icon icon={faMoneyBill} />}
            count={<span>{formatCurrency(averagePerClient)}</span>}
            countClassName="text-info"
          />
        </Col>
      </Row>

      <div className="payment-batches-wizard-clients-scroll mb-3">
        {clients.map((client) => (
          <ClientCard key={client.client_id} client={client} />
        ))}
      </div>

      <Alert color="danger" className="mb-0">
        <div className="d-flex align-items-start">
          <Icon icon={faExclamationTriangle} className="me-2 mt-1" />
          <div className="flex-grow-1">
            <div className="fw-semibold mb-2">This action cannot be undone.</div>
            <div className="mb-3">
              Submitting will create a payment batch and request payment from each listed client&apos;s mandate
              immediately. Failed payments cannot be reversed from this screen.
            </div>
            <div className="form-check">
              <Input
                type="checkbox"
                id="payment-batches-wizard-acknowledge"
                className="form-check-input"
                checked={acknowledged}
                onChange={(event) => onChange?.('acknowledged', event.target.checked)}
                data-cy="payment-batches-wizard-acknowledge-checkbox"
              />
              <Label
                for="payment-batches-wizard-acknowledge"
                className="form-check-label fw-semibold"
              >
                I understand this action cannot be undone
              </Label>
            </div>
          </div>
        </div>
      </Alert>
    </div>
  );
};

export default Confirm;
